import React, { useState } from "react";

import { faXmark } from "@fortawesome/pro-light-svg-icons";

import { useAwsAccountInfo } from "src/adminPanel/queries";
import { useNavigableWorkspaces } from "src/api/queries";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { ExternalLink } from "src/base-components/ExternalLink";
import { LoadingView } from "src/base-components/LoadingView";
import { Select } from "src/base-components/Select";
import { Tabs } from "src/base-components/Tabs";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Accordion } from "src/design-system/Accordion";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { JobsDebugSection } from "src/powertools/JobsDebugSection";
import { PendingDecisionsDebugSection } from "src/powertools/PendingDecisionsDebugSection";
import { UserRolesDebugSection } from "src/powertools/UserRolesDebugSection";
import { useWsInfo } from "src/powertools/useWsInfo";
import {
  getAgentLogsInsightsLink,
  getCloudFormationStackLink,
  getInfoEndpoint,
  getLambdaLink,
  getS3BucketLink,
} from "src/powertools/utils";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  onRequestClose?: () => void;
};

type DebugToolsProps = {
  workspace: WorkspaceDataplane;
};

const ACCESS_LEVELS = [
  "core-engineer",
  "on-call-engineer-read",
  "on-call-engineer-write",
  "solution-engineer",
  "admin",
] as const;

const SummaryRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="flex flex-col">
    <dt className="font-inter-medium-12px text-gray-400 uppercase tracking-wide">
      {label}
    </dt>
    <dd className="font-inter-normal-12px text-gray-800">{value}</dd>
  </div>
);

const ResourceGroup: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div>
    <div className="mb-2 font-inter-medium-12px text-gray-400 uppercase tracking-wide">
      {title}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const ResourceLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <ExternalLink
    className="rounded border border-gray-200 px-2 py-1 font-inter-normal-12px text-gray-800 hover:bg-gray-50"
    href={href}
  >
    {children}
  </ExternalLink>
);

export const DebugTools: React.FC<DebugToolsProps> = ({ workspace }) => {
  const wsInfo = useWsInfo(workspace);
  const awsAccountInfo = useAwsAccountInfo(workspace.aws_account_id || "");
  const [accessLevel, setAccessLevel] = useState<string>("core-engineer");
  const cfnStackName = wsInfo.data?.stack_name || "";
  const region = wsInfo.data?.region || "";
  const egressIps = wsInfo.data?.egress_ips ?? [];
  const lambdas = wsInfo.data?.function_name
    ? {
        router: wsInfo.data.function_name,
        runner: wsInfo.data.function_name.replace("router", "runner"),
        copilot: wsInfo.data.function_name.replace("router", "copilot"),
        connect: wsInfo.data.function_name.replace("flow-router", "connect"),
        exporter: wsInfo.data.function_name.replace("flow-router", "exporter"),
        archiver: wsInfo.data.function_name.replace("flow-router", "archiver"),
        stitcher: wsInfo.data.function_name.replace("flow-router", "stitcher"),
        llm_inference: `llm-inference-${cfnStackName}-lambda`,
        files: `files-${cfnStackName}-lambda`,
        batch_archiver: `history-${cfnStackName}v2-batch-archiver`,
        history_exporter: `history-exporter-${cfnStackName}v2`,
      }
    : {
        router: "",
        runner: "",
        copilot: "",
        connect: "",
        exporter: "",
        archiver: "",
        batch_archiver: "",
        migrator: "",
        llm_inference: "",
        files: "",
        history_exporter: "",
      };

  const accountName = awsAccountInfo.data?.name || "";
  const accountNameWithSuffix =
    accountName && accessLevel ? `${accountName}.${accessLevel}` : "";

  const getAwsAccountLabel = (): string => {
    if (awsAccountInfo.isLoading) return "Loading…";
    if (awsAccountInfo.isError) return "Error loading account info";
    if (!awsAccountInfo.data) return "N/A";
    return [awsAccountInfo.data.account_id, accountName && `(${accountName})`]
      .filter(Boolean)
      .join(" ");
  };

  const handleCopy = (value: string) => {
    if (value) {
      navigator.clipboard.writeText(value);
      toastActions.success({
        title: "Copied to clipboard",
        description: value,
      });
    }
  };

  return (
    <div className="justify-around px-10">
      <Accordion defaultValue={["summary", "on-call-access", "aws-resources"]}>
        <Accordion.Item label="Summary" value="summary">
          <dl className="grid grid-cols-3 gap-x-6 gap-y-2">
            <SummaryRow label="AWS Region" value={workspace.aws_region} />
            <SummaryRow
              label="Egress IPs"
              value={
                egressIps.length > 0 ? (
                  <div className="flex gap-1">
                    {egressIps.map((ip) => (
                      <div key={ip} className="flex items-center">
                        {ip}
                        <CopyTextIcon value={ip} feedback="inline" size="3xs" />
                      </div>
                    ))}
                  </div>
                ) : (
                  "N/A"
                )
              }
            />
            <SummaryRow label="AWS Account" value={getAwsAccountLabel()} />
            <SummaryRow
              label="Lambda version"
              value={workspace.flow_services_version}
            />
            <SummaryRow label="Status" value={workspace.status} />
            <SummaryRow
              label="Stack last event"
              value={wsInfo.data?.last_updated_at || "N/A"}
            />
            <SummaryRow
              label="Info endpoint"
              value={
                <ExternalLink href={getInfoEndpoint(workspace)}>
                  open
                </ExternalLink>
              }
            />
          </dl>
        </Accordion.Item>

        <Accordion.Item label="User roles" value="user-roles">
          <UserRolesDebugSection />
        </Accordion.Item>

        {awsAccountInfo.data && (
          <Accordion.Item label="On-call access" value="on-call-access">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-56">
                <label
                  className="mb-1 block font-inter-medium-12px text-gray-500"
                  htmlFor="access-level-select"
                >
                  Access level
                </label>
                <Select
                  dataLoc="access-level-select"
                  options={ACCESS_LEVELS.map((level) => ({
                    key: level,
                    value: level,
                  }))}
                  placeholder="Select access level"
                  value={accessLevel}
                  onChange={(value) => setAccessLevel(value || "core-engineer")}
                />
              </div>
              <Button
                isDisabled={!accountNameWithSuffix}
                variant="primary"
                onClick={() => handleCopy("login " + accountNameWithSuffix)}
              >
                Copy login cmd
              </Button>
              <Button
                isDisabled={!accountNameWithSuffix}
                variant="secondary"
                onClick={() => handleCopy("assume " + accountNameWithSuffix)}
              >
                Copy assume cmd
              </Button>
            </div>
            <p className="mt-2 font-inter-normal-12px text-gray-400">
              Format:{" "}
              <code className="font-code-12">
                {accountNameWithSuffix || "—"}
              </code>
            </p>
          </Accordion.Item>
        )}

        <Accordion.Item label="AWS resources" value="aws-resources">
          <div className="space-y-4">
            <ResourceGroup title="Infrastructure">
              <ResourceLink
                href={getCloudFormationStackLink(cfnStackName, region)}
              >
                CloudFormation stack
              </ResourceLink>
              {wsInfo.data?.error_log_insights_query_link && (
                <ResourceLink href={wsInfo.data.error_log_insights_query_link}>
                  Error Log Insights
                </ResourceLink>
              )}
              <ResourceLink
                href={getAgentLogsInsightsLink(cfnStackName, region)}
              >
                Agent Logs
              </ResourceLink>
              <ResourceLink
                href={getS3BucketLink(`files-${cfnStackName}-bucket`, region)}
              >
                Files S3 bucket
              </ResourceLink>
            </ResourceGroup>

            <ResourceGroup title="Lambda functions">
              {Object.entries(lambdas)
                .filter(([, lambda]) => lambda)
                .map(([key, lambda]) => (
                  <ResourceLink key={key} href={getLambdaLink(region, lambda)}>
                    {key}
                  </ResourceLink>
                ))}
            </ResourceGroup>
          </div>
        </Accordion.Item>
      </Accordion>

      {workspace.base_url && (
        <div className="mt-6">
          <Tabs
            defaultActiveKey="jobs"
            tabListClassName="flex gap-x-4 border-gray-200 border-b mb-4"
            tabs={[
              {
                key: "jobs",
                label: "Jobs",
                content: <JobsDebugSection workspace={workspace} />,
              },
              {
                key: "pending-decisions",
                label: "Pending Decisions",
                content: <PendingDecisionsDebugSection workspace={workspace} />,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export const DebugSidePane: React.FC<Props> = ({ onRequestClose }) => {
  const workspacesResult = useNavigableWorkspaces();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const currentWs = workspacesResult.data?.find((ws) => ws.id === wsId);

  const renderHeader = () => (
    <div className="mb-5 flex items-start justify-around px-10">
      <div className="grow">
        <div className="mb-3 font-inter-semibold-16px text-gray-800">
          @taktile Debug Pane
        </div>
      </div>
      {onRequestClose && (
        <IconButton icon={faXmark} size="md" onClick={onRequestClose} />
      )}
    </div>
  );

  return (
    <div className="flex h-full w-full max-w-7xl flex-col border-gray-200 border-l bg-white py-8">
      {renderHeader()}
      <div className="mt-4 min-h-0 flex-1 overflow-auto">
        <LoadingView
          queryResult={workspacesResult}
          renderUpdated={() =>
            currentWs ? <DebugTools workspace={currentWs} /> : <div />
          }
        />
      </div>
    </div>
  );
};
